import * as amplitude from "@amplitude/analytics-browser";
import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  OutboundViewCountsContext,
  OutboundViewsContext,
} from "@redotech/redo-merchant-fulfillment-app/outbound-views";
import { OrderManagementPermissions } from "@redotech/redo-model/permissions";
import { OutboundSystemView } from "@redotech/redo-model/views/default-outbound-views";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import {
  TreeItem,
  TreeItemType,
} from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { PlatformEvents } from "../../../../platform-events";
import {
  FULFILLMENT_ICON,
  FULFILLMENT_ICON_FILLED,
  OrderManagementSuiteStatusesContext,
} from "../../../../redo-suite/order-management-cloud";
import { useIsChildRouteActive } from "./use-child-route-active";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

/**
 * Routes that are NOT Fulfillment children - includes Operations-level routes
 * and Outbound's direct children that are siblings of Fulfillments.
 */
const NON_FULFILLMENT_ROUTES = [
  // Operations-level routes
  "dashboard",
  "orders",
  "about",
  // Outbound direct children (siblings of Fulfillments)
  "batch-history",
  "pickups",
  "eod-manifest",
  "admin-pick-sessions",
  "pick-sessions",
  "create-pick-session",
  "print-jobs",
  "rate-calculator",
  "scan",
];

export const FulfillmentItem = memo(function FulfillmentItem({
  autoExpand,
}: {
  autoExpand?: boolean;
}) {
  const team = useRequiredContext(TeamContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const { omsEnabled } = useContext(OrderManagementSuiteStatusesContext);
  const urlPrefix = routes.ordersOutboundLabelsHref;

  const outboundViewsContext = useRequiredContext(OutboundViewsContext);
  const outboundViewCountsContext = useContext(OutboundViewCountsContext);

  const canViewShippingTabs = useHasPermission(
    OrderManagementPermissions.MANAGE_SHIPMENTS,
  );

  const canViewRewards =
    team.settings.outboundLabels?.pointsSettings?.enabled &&
    canViewShippingTabs;

  // Auto-expand when on any Fulfillment child route
  const excludedRoutes = useMemo(() => NON_FULFILLMENT_ROUTES, []);
  const isAnyChildActive = useIsChildRouteActive(
    routes.ordersOutboundLabelsHref,
    excludedRoutes,
  );

  const allViews = useMemo(() => {
    return [...outboundViewsContext.views];
  }, [outboundViewsContext]);

  const allFolders = useMemo(() => {
    return [...outboundViewsContext.folders];
  }, [outboundViewsContext]);

  const viewsWithoutFolders = useMemo(() => {
    return allViews.filter((view) => !view.folderId);
  }, [allViews]);

  const foldersWithViews = useMemo<
    FolderWithView<"fulfillment-groups">[]
  >(() => {
    return allFolders.map((folder) => {
      return {
        folder,
        views: allViews.filter((view) => view.folderId === folder._id),
      };
    });
  }, [allFolders, allViews]);

  const awaitingShipmentCount = useMemo(() => {
    if (!outboundViewCountsContext?.viewCounts) {
      return undefined;
    }
    const awaitingShipmentView = allViews.find(
      (view) => view.name === OutboundSystemView.AWAITING_SHIPMENT,
    );
    if (!awaitingShipmentView) {
      return undefined;
    }
    const count =
      outboundViewCountsContext.viewCounts[awaitingShipmentView._id] || 0;
    return count > 0 ? count : undefined;
  }, [allViews, outboundViewCountsContext?.viewCounts]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: outboundViewCountsContext?.viewCounts,
      folderCounts: outboundViewCountsContext?.folderCounts,
      hideZeroNotifications: true,
    });
  }, [
    viewsWithoutFolders,
    foldersWithViews,
    urlPrefix,
    outboundViewCountsContext?.viewCounts,
    outboundViewCountsContext?.folderCounts,
  ]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    outboundViewsContext.openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);

    const { views, folders } = treeToViewsAndFolders<"fulfillment-groups">(
      flattenedItems,
      allViews,
      allFolders,
    );

    outboundViewsContext.updateOrdering(views, folders);
  });

  const aiSlidePanel = useAiSlidePanel();

  if (!omsEnabled || !canViewShippingTabs) {
    return null;
  }

  return (
    <RedoSecondaryNavItem
      action={{ type: "button" }}
      anyChildSelected={isAnyChildActive || (autoExpand ?? false)}
      FilledIcon={FULFILLMENT_ICON_FILLED}
      friendlyName="Fulfillments"
      id="fulfillment"
      notificationCount={awaitingShipmentCount}
      UnfilledIcon={FULFILLMENT_ICON}
    >
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getOrdersOutboundLabelsSubRoute("bulk-fulfillments"),
        }}
        friendlyName="Bulk shipments"
        id="fulfillment-bulk-shipments"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getOrdersOutboundLabelsSubRoute("rewards"),
        }}
        friendlyName="Rewards"
        hidden={!canViewRewards}
        id="fulfillment-rewards"
      />
      <SortableTree
        items={sortableItems}
        onItemClick={(item) => {
          amplitude.track(PlatformEvents.NAV_SIDEBAR_ITEM_CLICKED, {
            section: "shipping",
            item:
              item.type === TreeItemType.Folder
                ? "fulfillment-folder"
                : "fulfillment-saved-view",
          });
        }}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            aiSlidePanel.open({});
          },
        }}
        friendlyName="+ Manage views with AI"
        id="fulfillment-manage-views-ai"
      />
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: routes.getOrdersOutboundLabelsSubRoute("create"),
        }}
        friendlyName="+ Add new view"
        id="fulfillment-create-view"
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            outboundViewsContext.openCreateFolderModal();
          },
        }}
        friendlyName="+ Add new folder"
        id="fulfillment-create-folder"
      />
    </RedoSecondaryNavItem>
  );
});
